/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import './content.css';
import React, { useEffect, useState } from 'react';
import { ControlMapper } from '../controls';
import '@ui5/webcomponents-icons/dist/da-2.js';
import '@ui5/webcomponents-icons/dist/home.js';
import { actions, fetchTasks } from '../store/Products';
import '@ui5/webcomponents-icons/dist/open-command-field.js';
import '@ui5/webcomponents-icons/dist/close-command-field.js';
import '@ui5/webcomponents-icons/dist/to-be-reviewed.js';
import { useTranslations } from '../hooks/useTranslations';
import { ControlTypeContent } from '@/types';
import { useAppDispatch, useAppSelector } from '@/hooks';

declare global {
    interface Window {
        sap: any;
    }
}
const MainArea: React.FC = () => {
    const productList = useAppSelector(state => state.productList);
    const currentRunId = productList.currentRunId;
    const flowId = productList.currentFlowId;
    const stepId = productList.currentStep?.id;
    const [navigationData, setNavigationData] = useState<any>();
    const { t } = useTranslations();
    const dispatch = useAppDispatch();

    const fetchNavigationData = async () => {
        try {
            if (!flowId || !currentRunId)
                return {
                    tasks: [],
                };
            const queryParams = new URLSearchParams();
            queryParams.append('flowId', flowId);
            queryParams.append('runId', currentRunId);
            if (stepId) {
                queryParams.append('stepId', stepId);
            }
            if (productList.currentPhase?.id === productList.currentStep.phaseId) {
                return await dispatch(fetchTasks(queryParams.toString())).unwrap();
            }
            return [];
        } catch (error) {
            console.error('Error loading JSON data:', error);
        }
    };

    const allPageData = async () => {
        try {
            const data: any = await fetchNavigationData();
            const questions: any = [];
            data?.tasks?.forEach((question: any) => {
                if (question?.phase && question?.step) {
                    question['phaseId'] = question.phase.id;
                    question['stepId'] = question.step.id;
                    question['type'] = 'dbLayout';
                    if (question.videoHelpLink) {
                        dispatch(actions.setShowHelpVideo(true));
                    }

                    if (
                        question?.controlType?.name === 'AdditionalUser' ||
                        question?.controlType?.name === 'CompanyLogo' ||
                        question?.controlType?.name === 'CommodityCodeLayout' ||
                        question?.controlType?.name === 'CompanyInformation' ||
                        question?.controlType?.name === 'SuccessMeasures' ||
                        question?.controlType?.name === 'Experience' ||
                        question?.controlType?.name === 'TableLayout' ||
                        question?.controlType?.name === 'reviewLayout' ||
                        question?.controlType?.name === 'Video' ||
                        question?.controlType?.name === 'supplierLevel' ||
                        question?.controlType?.name === 'IllustrationImage'
                    ) {
                        question['type'] = question?.controlType?.name;
                    }
                    if (question.phase.name === 'Best Practices' && question.step.name === 'System Integration') {
                        question['type'] = 'BestPractices';
                    }
                    questions.push(question);
                }
            });

            const taskDetails = questions.find((question: any) => question.stepId === productList.currentStep.id);
            if (taskDetails) {
                taskDetails.personalizationConfiguration = data.values[taskDetails?.productConfigurationKey];
                if (taskDetails.controlType?.name === 'TableLayout') {
                    addTableLayoutData(taskDetails);
                }
                setNavigationData(taskDetails);
                if (taskDetails?.videoHelpLink) {
                    dispatch(actions.setShowHelpVideo(true));
                } else {
                    dispatch(actions.setShowHelpVideo(false));
                }
                const controlTypeData: ControlTypeContent = taskDetails?.controlType;
                dispatch(actions.setCurrentControlType(controlTypeData));
            }
        } catch (error) {
            console.error('Error loading JSON data:', error);
        }
    };

    useEffect(() => {
        if (currentRunId && flowId && productList.currentStep) {
            allPageData();
        }
    }, [currentRunId, flowId, productList.currentStep]);

    const addTableLayoutData = (taskDetails: any) => {
        const tableData = {
            tableTitle: 'COMMODITY_CODES',
            tableColumn: [
                {
                    title: t('DOMAIN'),
                    dataIndex: 'domain',
                },
                {
                    title: t('UNIQUE_ID'),
                    dataIndex: 'uniqueKey',
                },
                {
                    title: t('NAME'),
                    dataIndex: 'name',
                    sorter: 'alphabeticalOrder',
                },
                {
                    title: t('ENABLED'),
                    dataIndex: 'enabled',
                },
                {
                    title: t('PARENT_ID'),
                    dataIndex: 'parentUniqueKey',
                },
            ],
            filterableColumns: [t('UNIQUE_ID'), t('NAME'), t('PARENT_ID')],
        };
        taskDetails.tableData = tableData;
    };

    return (
        <div className="content__data" data-sap-ui-fastnavgroup="true">
            {navigationData &&
                (navigationData.type === 'AdditionalUser' ? (
                    <ControlMapper {...navigationData} />
                ) : (
                    <div style={{ height: '100%', width: '100%' }}>
                        <ControlMapper {...navigationData} />
                    </div>
                ))}
        </div>
    );
};

export default MainArea;
